import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../../constants/documentTheme';
import { partnerLinkSx, partnerOxbloodBtnSx } from '../PartnerListChrome';
import { BILLING_STATUS_SX, COLLECTION_METHODS } from '../../utils/groupBilling';

/** Returns [confirm(message) => Promise<boolean>, dialogElement]. */
export function useConfirm() {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const resolver = useRef(null);
  const confirm = useCallback((message, confirmLabel) => new Promise((resolve) => {
    resolver.current = resolve;
    setState({ message, confirmLabel });
  }), []);
  const close = (value) => {
    resolver.current?.(value);
    resolver.current = null;
    setState(null);
  };
  const element = (
    <Dialog open={Boolean(state)} onClose={() => close(false)} maxWidth="xs" fullWidth data-testid="group-confirm-dialog">
      <DialogTitle sx={{ fontFamily: DOCUMENT.display }}>{t('groupBilling.confirmTitle')}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14 }}>{state?.message}</Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => close(false)} sx={partnerLinkSx}>{t('common.cancel')}</Button>
        <Button onClick={() => close(true)} sx={partnerOxbloodBtnSx} data-testid="group-confirm-ok">
          {state?.confirmLabel || t('groupBilling.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
  return [confirm, element];
}

export const eyebrowSx = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink3,
};

export const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 0 } };

export function BillingStatusChip({ status, testId }) {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      variant="outlined"
      label={t(`groupBilling.status.${status}`, { defaultValue: status })}
      data-testid={testId}
      sx={{ borderRadius: 0, ...(BILLING_STATUS_SX[status] || BILLING_STATUS_SX.no_charges) }}
    />
  );
}

export function OutlineChip({ label, testId }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      data-testid={testId}
      sx={{ borderRadius: 0, borderColor: DOCUMENT.ruleHard, color: DOCUMENT.ink2 }}
    />
  );
}

export function SectionCard({ title, action, children, testId, sx }) {
  return (
    <Box
      data-testid={testId}
      sx={{ border: `1px solid ${DOCUMENT.ruleHard}`, bgcolor: DOCUMENT.surface, p: { xs: 2, md: 2.5 }, ...sx }}
    >
      {(title || action) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 21 }}>{title}</Typography>
          {action}
        </Box>
      )}
      {children}
    </Box>
  );
}

/** "How the client pays" tiles, same look as the package editor. */
export function CollectionMethodTiles({ value, onChange, testIdPrefix = 'billing-collection', compact = false }) {
  const { t } = useTranslation();
  return (
    <Box
      role="radiogroup"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
        gap: '1px',
        bgcolor: DOCUMENT.ruleHard,
        border: `1px solid ${DOCUMENT.ruleHard}`,
      }}
    >
      {COLLECTION_METHODS.map((key) => {
        const pressed = value === key;
        return (
          <Box
            key={key}
            component="button"
            type="button"
            role="radio"
            aria-checked={pressed}
            data-testid={`${testIdPrefix}-${key}`}
            onClick={() => onChange(key)}
            sx={{
              background: pressed ? DOCUMENT.oxblood : DOCUMENT.surface,
              color: pressed ? DOCUMENT.ivory : DOCUMENT.ink,
              border: 0,
              font: 'inherit',
              textAlign: 'left',
              px: 2,
              py: compact ? 1.25 : 1.75,
              cursor: 'pointer',
            }}
          >
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: compact ? 15 : 17, color: 'inherit', mb: compact ? 0 : 0.5 }}>
              {t(`groupBilling.collection.${key}`)}
            </Typography>
            {!compact && (
              <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: pressed ? '#E6D4C4' : DOCUMENT.ink2 }}>
                {t(`groupBilling.collection.${key}Hint`)}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export function SummaryTile({ label, value, sub, testId }) {
  return (
    <Box data-testid={testId} sx={{ bgcolor: DOCUMENT.surface, p: 2, textAlign: 'center' }}>
      <Typography sx={eyebrowSx}>{label}</Typography>
      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 24, mt: 0.5 }}>{value}</Typography>
      {sub ? <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>{sub}</Typography> : null}
    </Box>
  );
}
